import type { CampaignMetrics, CampaignsReportResponse } from "@gioia365/contracts";
import { REPORT_KPI_UNITS, reportKpiValue, reportTargetStatus, reportTargetWord, type ReportKpiId, type ReportTargets } from "@gioia365/domain/report-kpis";
import type { MessageKey } from "@gioia365/i18n";
import { Fragment, useId } from "react";
import { DataTable } from "../design/components/DataTable";
import { formatCount } from "../i18n/format";
import { useLocale, useT } from "../i18n/locale";
import { campaignReportMetrics, campaignText, formatKpi, salesCountText, targetOf, targetText, targetWordText } from "./report-format";
import { DeltaBadge } from "./SalesSummary";

// «Le campagne» (piano M7, Task 5; mock-up `renderCampagne`, `summaryTable`, `summaryCards`; D8): una riga per campagna o lista, in
// ordine di valore e poi di Lead nuovi (l'ordine lo dà il server), e «Tutte le campagne». Le sette colonne del mock-up più il close rate, ognuna
// con la sua spiegazione al passaggio; le frecce del confronto sui Lead nuovi, le disco, le vendite, il close rate e il valore, con le stesse regole di
// «Commerciali» (`reportDelta` del dominio), mai su «Rientrati» e «Valore medio». Il valore medio è la formula del dominio
// (`averageValue`, valore ÷ vendite), anche nel totale. Sopra 860 px la tabella, sotto le stesse righe come schede aperte, con le
// vendite nel riassunto. Dopo «Vendite» il close rate della campagna (vendite ÷ demo effettuate, `closeRate` del dominio, «—» senza
// demo effettuate), con la freccia e, come in «Commerciali», il colore verde o arancione, il segno e la parola contro l'obiettivo di
// Impostazioni → «Obiettivi» (`targets` della risposta).

type ColumnId = "fresh" | "back" | "disco" | "closings" | "closeRate" | "value" | "average";

/** Le colonne: il numero del Report che le calcola (nessuno per «Rientrati», un conteggio e basta) e se hanno la freccia. */
const COLUMNS: ReadonlyArray<{ id: ColumnId; kpi: ReportKpiId | null; arrow: boolean }> = [
  { id: "fresh", kpi: "leads", arrow: true },
  { id: "back", kpi: null, arrow: false },
  { id: "disco", kpi: "discoBooked", arrow: true },
  { id: "closings", kpi: "closings", arrow: true },
  { id: "closeRate", kpi: "closeRate", arrow: true },
  { id: "value", kpi: "value", arrow: true },
  { id: "average", kpi: "averageValue", arrow: false },
];

const COLUMN_TEXTS: Readonly<Record<ColumnId, { label: MessageKey; how: MessageKey }>> = {
  fresh: { label: "reports.campaigns.columns.fresh.label", how: "reports.campaigns.columns.fresh.how" },
  back: { label: "reports.campaigns.columns.back.label", how: "reports.campaigns.columns.back.how" },
  disco: { label: "reports.campaigns.columns.disco.label", how: "reports.campaigns.columns.disco.how" },
  closings: { label: "reports.campaigns.columns.closings.label", how: "reports.campaigns.columns.closings.how" },
  closeRate: { label: "reports.campaigns.columns.closeRate.label", how: "reports.campaigns.columns.closeRate.how" },
  value: { label: "reports.campaigns.columns.value.label", how: "reports.campaigns.columns.value.how" },
  average: { label: "reports.campaigns.columns.average.label", how: "reports.campaigns.columns.average.how" },
};

type Column = (typeof COLUMNS)[number];

/** L'obiettivo di una colonna (soltanto il close rate ne ha uno) e com'è il numero contro di lui; nullo senza numero o senza obiettivo. */
function targetStatusOf(column: Column, metrics: CampaignMetrics, targets: ReportTargets) {
  if (column.kpi === null) return null;
  const target = targetOf(targets, column.kpi);
  const value = reportKpiValue(column.kpi, campaignReportMetrics(metrics));
  const status = reportTargetStatus(value, target);
  if (value === null || target === undefined || status === null) return null;
  return { status, target, word: reportTargetWord(value, target), kpi: column.kpi };
}

type Row = { key: string; label: string; current: CampaignMetrics; previous: CampaignMetrics; total?: boolean };

type Props = {
  report: CampaignsReportResponse;
  /** Il periodo scritto, per la didascalia. */
  range: string;
  /** Di chi sono i numeri («Tutto il team.», le persone scelte, i tuoi). */
  whose: string;
};

export function CampaignsTable({ report, range, whose }: Props) {
  const t = useT();
  const { locale } = useLocale();
  const titleId = useId();
  const rows: Row[] = report.campaigns.map((row) => ({ key: JSON.stringify([row.source, row.campaign]), label: campaignText(t, row.source, row.campaign), current: row.current, previous: row.previous }));
  rows.push({ key: "total", label: t("reports.campaigns.total"), current: report.totals.current, previous: report.totals.previous, total: true });

  /** L'obiettivo di una colonna, scritto («obiettivo 20%»), se ne ha uno. */
  const goalOf = (column: Column) => {
    const target = column.kpi === null ? undefined : targetOf(report.targets, column.kpi);
    return column.kpi !== null && target !== undefined ? targetText(t, locale, column.kpi, target) : null;
  };

  /** Il numero di una colonna, scritto. */
  const valueOf = (column: Column, metrics: CampaignMetrics) =>
    column.kpi === null ? formatCount(locale, metrics.back) : formatKpi(locale, REPORT_KPI_UNITS[column.kpi], reportKpiValue(column.kpi, campaignReportMetrics(metrics)));

  return (
    <section className="g-report-section" aria-labelledby={titleId}>
      <div className="g-report-head">
        <div>
          <h2 id={titleId}>{t("reports.campaigns.title")}</h2>
          <p className="g-report-hint">{t("reports.campaigns.intro", { whose })}</p>
        </div>
      </div>
      <div className="g-report-table">
        <DataTable caption={t("reports.campaigns.caption", { range })} captionHidden>
          <thead>
            <tr>
              <th scope="col">{t("reports.campaigns.firstHead")}</th>
              {COLUMNS.map((column) => {
                const goal = goalOf(column);
                return (
                  <th key={column.id} scope="col" title={t(COLUMN_TEXTS[column.id].how)}>
                    {t(COLUMN_TEXTS[column.id].label)}
                    {goal ? <span className="g-table__sub">{goal}</span> : null}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className={row.total ? "g-report-total" : undefined}>
                <th scope="row">{row.label}</th>
                {COLUMNS.map((column) => {
                  const goal = targetStatusOf(column, row.current, report.targets);
                  const said = goal ? { target: targetText(t, locale, goal.kpi, goal.target), word: targetWordText(t, goal.word) } : null;
                  return (
                    <td key={column.id} className="g-report-num" data-status={goal?.status} title={said ? t("reports.target.cell", said) : undefined}>
                      <span className="g-report-num__value">{valueOf(column, row.current)}</span>
                      {goal && said ? (
                        <>
                          <span className="g-report-mark" aria-hidden="true">
                            {goal.status === "ok" ? t("reports.marks.ok") : t("reports.marks.below")}
                          </span>
                          <span className="g-visually-hidden">{t("reports.target.hidden", said)}</span>
                        </>
                      ) : null}
                      {column.arrow && column.kpi !== null ? (
                        <span className="g-table__sub">
                          <DeltaBadge id={column.kpi} current={campaignReportMetrics(row.current)} previous={campaignReportMetrics(row.previous)} />
                        </span>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </DataTable>
      </div>
      <div className="g-report-cards">
        {rows.map((row) => (
          <details key={row.key} className="g-report-card" data-total={row.total ? "true" : undefined} open>
            <summary>
              <span className="g-report-card__name">{row.label}</span>
              <span className="g-report-card__key">{salesCountText(t, locale, row.current.closings)}</span>
            </summary>
            <dl>
              {COLUMNS.map((column) => {
                const goal = targetStatusOf(column, row.current, report.targets);
                const target = goalOf(column);
                return (
                  <Fragment key={column.id}>
                    <dt>
                      {t(COLUMN_TEXTS[column.id].label)}
                      {target ? <span className="g-table__sub">{target}</span> : null}
                    </dt>
                    <dd data-status={goal?.status}>
                      {valueOf(column, row.current)}
                      {goal ? (
                        <>
                          <span className="g-report-mark" aria-hidden="true">
                            {goal.status === "ok" ? t("reports.marks.ok") : t("reports.marks.below")}
                          </span>
                          <span className="g-visually-hidden">{t("reports.target.cardHidden", { word: targetWordText(t, goal.word) })}</span>
                        </>
                      ) : null}
                    </dd>
                  </Fragment>
                );
              })}
            </dl>
          </details>
        ))}
      </div>
    </section>
  );
}
